import { getAuthOwnerIdentity, isAuthOwnerCurrent, waitForAuthOwnerReady } from "@istari-frontend/auth";
import { TransferCancelledError } from "@istari/istari-client";
import type { MutateOptions } from "@tanstack/react-query";

import { getUploadSessionVersion } from "./upload-payloads";

export type UploadSession = { owner: string | undefined; sessionVersion: number };

export function captureUploadSession(): UploadSession {
  return { owner: getAuthOwnerIdentity(), sessionVersion: getUploadSessionVersion() };
}

export function isUploadSessionCurrent(session: UploadSession): boolean {
  return session.sessionVersion === getUploadSessionVersion() && isAuthOwnerCurrent(session.owner);
}

export function assertUploadSessionCurrent(session: UploadSession): void {
  if (!isUploadSessionCurrent(session)) throw new TransferCancelledError("Upload session changed");
}

export async function awaitUploadSessionReady(session: UploadSession): Promise<void> {
  assertUploadSessionCurrent(session);
  if (!(await waitForAuthOwnerReady(session.owner))) throw new TransferCancelledError("Upload session changed");
  assertUploadSessionCurrent(session);
}

export function uploadSessionCallbacks<TData, TError, TVariables, TContext>(
  callbacks?: MutateOptions<TData, TError, TVariables, TContext>,
  session = captureUploadSession(),
): MutateOptions<TData, TError, TVariables, TContext> {
  return {
    onSuccess: (...args) => {
      if (isUploadSessionCurrent(session)) callbacks?.onSuccess?.(...args);
    },
    onError: (...args) => {
      if (isUploadSessionCurrent(session)) callbacks?.onError?.(...args);
    },
    onSettled: (...args) => {
      if (isUploadSessionCurrent(session)) callbacks?.onSettled?.(...args);
    },
  };
}
